import { normalizeLanguage } from "../../../i18n/config";

/**
 * Фиксированный (системный) список этапов программы — типов приёма.
 *
 * Список ЗАШИТ в код: из интерфейса его нельзя дополнить, переименовать или
 * удалить. В плане лечения для каждого номера приёма хранится только выбранное
 * название (ru + en), а арабская подпись берётся отсюда при отображении.
 *
 * Используется везде, где раньше был «справочник» из таблицы
 * `treatment_plan_appointment_type`: меню выбора типа в схеме лечения,
 * заголовки столбцов таблицы, печатные формы.
 *
 * Порядок записей = порядок пунктов меню. Число приёмов в плане этим списком
 * не ограничивается: один и тот же тип можно поставить нескольким приёмам.
 *
 * ВНИМАНИЕ: тот же список есть на бэкенде (`_appointment_type_options` в
 * `modules/patient/treatment_plan.py`). При изменении править оба места.
 */
export const STAGE_TYPES = [
    { name: "Консультация", nameEn: "Consultation", nameAr: "استشارة" },
    { name: "Диагностика", nameEn: "Diagnostics", nameAr: "تشخيص" },
    { name: "Процедура 1", nameEn: "Procedure 1", nameAr: "إجراء 1" },
    { name: "Повторный приём", nameEn: "Follow-up", nameAr: "زيارة متابعة" },
    { name: "Процедура 2", nameEn: "Procedure 2", nameAr: "إجراء 2" },
    { name: "Процедура 3", nameEn: "Procedure 3", nameAr: "إجراء 3" },
    { name: "Завершение", nameEn: "Final", nameAr: "إنهاء" },
];

const normalizeKey = (value) =>
    String(value ?? "")
        .trim()
        .toLowerCase();

/** Название этапа на языке интерфейса (ru / en / ar) с откатом. */
export const getStageTypeLabel = (type, language) => {
    if (!type) return "";
    const lang = normalizeLanguage(language);
    if (lang === "ar") return type.nameAr || type.nameEn || type.name || "";
    if (lang === "en") return type.nameEn || type.name || "";
    return type.name || type.nameEn || "";
};

/**
 * Находит тип в фиксированном списке по сохранённым названиям (любой язык).
 * Нужен для планов, где тип был сохранён раньше: по названию восстанавливаем
 * запись списка и показываем её на текущем языке (в т.ч. арабском).
 */
export const findStageType = (name, nameEn) => {
    const keys = [normalizeKey(name), normalizeKey(nameEn)].filter(Boolean);
    if (!keys.length) return null;
    return (
        STAGE_TYPES.find((type) => {
            const candidates = [
                normalizeKey(type.name),
                normalizeKey(type.nameEn),
                normalizeKey(type.nameAr),
            ];
            return keys.some((key) => candidates.includes(key));
        }) || null
    );
};

/** Пункты меню выбора типа приёма (подписи — на языке интерфейса). */
export const getStageTypeOptions = (language) =>
    STAGE_TYPES.map((type) => ({
        name: type.name,
        nameEn: type.nameEn,
        label: getStageTypeLabel(type, language),
    }));
